import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Center, Flex, Heading, HStack, Icon, IconButton, Spinner, Text, VStack } from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaShieldAlt, FaTrash } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  useDeleteSourcingEventTemplateMutation,
  useListSourcingEventTemplatesQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { templateSummary } from "../../../common/library/templateSummary";
import { useRunsPublicProcurements } from "../../../common/organization/useRunsPublicProcurements";
import { useConfirmationModal } from "../../../common/useConfirmationModal";
import { urls } from "../../../urls";
import { useProcurementTerms } from "../../../common/terminology/useProcurementTerms";

// t('New template') t('Only administrators can edit templates') t('No templates yet')
// t('Create a template to reuse across procurements.')
// t('Delete template') t('Are you sure you want to delete this template?') t('Delete') t('Edit')

export const SourcingEventTemplates = () => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const runsPublicProcurements = useRunsPublicProcurements();
  const navigate = useNavigate();
  const authState = useLoggedInWithOrgContextState();
  const canManage = authState.selectedOrg.canEditDocumentTemplates;
  const isSystemAdmin = authState.me.isSystemAdmin;
  const { data, isLoading, error } = useListSourcingEventTemplatesQuery({});
  const templates = data?.templates ?? [];
  const [deleteTemplate] = useDeleteSourcingEventTemplateMutation();
  const { trigger, triggerSoft } = useApiError();
  const confirmationModal = useConfirmationModal();
  useEffect(() => {
    if (error) triggerSoft(error);
  }, [error, triggerSoft]);

  const onDelete = (id: string) => {
    confirmationModal.show({
      title: t("Delete template"),
      description: t("Are you sure you want to delete this template?"),
      confirmButtonText: t("Delete") ?? "",
      confirmButtonColor: "red",
      onConfirmed: async () => {
        try {
          await deleteTemplate({ id }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <VStack align="stretch" spacing="5">
      <Flex justify="space-between" align="center" gap="3">
        <Heading size="lg" color="smText">
          {t("Templates")}
        </Heading>
        {canManage && (
          <Button size="sm" colorScheme="teal" leftIcon={<AddIcon />} onClick={() => navigate(urls.v2.maler.new.go())} flexShrink={0}>
            {t("New template")}
          </Button>
        )}
      </Flex>
      <HStack color="smMuted" fontSize="sm" spacing="2">
        <Icon as={FaShieldAlt} />
        <Text>{t("Only administrators can edit templates")}</Text>
      </HStack>

      {isLoading && !data ? (
        <Center py="20">
          <Spinner color="smPrimary" />
        </Center>
      ) : templates.length === 0 ? (
        <Box
          borderWidth="1px"
          borderColor="smBorder"
          borderRadius="lg"
          bg="smBackgroundSecondary"
          py="12"
          px="6"
          textAlign="center"
        >
          <Text fontWeight="bold" mb="1">
            {t("No templates yet")}
          </Text>
          <Text color="smMuted" fontSize="sm">
            {terms.templateReuseHint}
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing="3">
          {templates.map((tmpl) => (
            <Flex
              key={tmpl.id}
              borderWidth="1px"
              borderColor="smBorder"
              borderRadius="xl"
              bg="smBackground"
              p="5"
              justify="space-between"
              align="center"
              gap="4"
            >
              <Box minW="0">
                <Text fontWeight="bold">{tmpl.name}</Text>
                {tmpl.category && (
                  <Text color="smMuted" fontSize="sm">
                    {tmpl.category}
                  </Text>
                )}
                <Text color="smMuted" fontSize="sm" mt="1">
                  {templateSummary(tmpl.content, t, terms, runsPublicProcurements)}
                </Text>
              </Box>
              {(tmpl.owner.global ? isSystemAdmin : canManage) && (
                <HStack flexShrink={0}>
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Icon as={FiEdit} />}
                    onClick={() => navigate(urls.v2.maler.edit.go(tmpl.id))}
                  >
                    {t("Edit template")}
                  </Button>
                  <IconButton
                    aria-label={t("Delete template")}
                    icon={<FaTrash />}
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => onDelete(tmpl.id)}
                  />
                </HStack>
              )}
            </Flex>
          ))}
        </VStack>
      )}
      {confirmationModal.modal}
    </VStack>
  );
};
